import { Inject, inject, Injectable } from '@angular/core';
import { QuickBooksService } from '@vendasta/quickbooks';
import { map, Observable, of, switchMap } from 'rxjs';
import { IntegrationType } from './model/connection-integration-detail';
import { MeetingSource } from '@vendasta/meetings';
import { CardDataService } from './card-data.service';
import {
  PLATFORM_INTEGRATIONS_NAMESPACE_INJECTION_TOKEN$,
  PLATFORM_INTEGRATIONS_PARTNER_ID_INJECTION_TOKEN$,
} from '@galaxy/platform-integrations/shared';
import { SSOService } from '@vendasta/sso';
import { LegacyIntegrationType, SocialConnectionLinks } from './model/social-connections.interface';
import { HttpClient } from '@angular/common/http';
import { AccountGroupApiService } from '@vendasta/account-group';
import { LegacyDomainService } from './common/legacy-domain.service';
import { IntegrationConnectionsDetail } from './model/Integration-connections-detail';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvironmentService } from '@galaxy/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class LegacyAuthService {
  private readonly quickBooksService = inject(QuickBooksService);
  private readonly cardService = inject(CardDataService);
  private readonly ssoService = inject(SSOService);
  private readonly httpClient = inject(HttpClient);
  private readonly accountGroupApiService = inject(AccountGroupApiService);
  private readonly legacyDomainService = inject(LegacyDomainService);
  private readonly userID = toSignal(this.userId$);
  private readonly partnerID = toSignal(this.partnerId$);
  private readonly environmentService = inject(EnvironmentService);

  constructor(
    @Inject(PLATFORM_INTEGRATIONS_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
    @Inject(PLATFORM_INTEGRATIONS_PARTNER_ID_INJECTION_TOKEN$) public readonly partnerId$: Observable<string>,
    @Inject('USER_ID') private userId$: Observable<string>,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  getAuthUrlByIntegrationType(
    accountGroupId: string,
    integrationType: LegacyIntegrationType,
    productType?: string,
  ): Observable<string> {
    const domain = this.document.location.origin;
    console.log(domain);
    const nextUrl = new URL(`/account/location/${accountGroupId}/settings/connections`, domain);

    const url = this.document.location.href;
    const urlParams = new URLSearchParams(new URL(url).search);
    const returnUrl = urlParams.get('returnUrl');

    return this.accountGroupApiService
      .getMulti({
        accountGroupIds: [accountGroupId],
        projectionFilter: { accountGroupExternalIdentifiers: true },
      })
      .pipe(
        map((response) => response.accountGroups[0]?.accountGroup?.accountGroupExternalIdentifiers?.partnerId),
        switchMap((partnerId) => {
          return this.httpClient
            .get<{ data: SocialConnectionLinks }>('/ajax/v1/get-auth-urls/', {
              params: {
                accountGroupId,
                nextUrl: nextUrl.href,
              },
            })
            .pipe(
              map((response) => {
                const serviceAuthURL = response.data[integrationType];
                if (productType === IntegrationType.GOOGLEMYBUSINESS || productType === IntegrationType.FACEBOOK) {
                  const typeAsDisplayName = this.legacyDomainService.getLegacyTypeAsDisplayName(productType);
                  const url = new URL(serviceAuthURL);
                  const nextUrl1 = new URLSearchParams(url.search).get('nextUrl');
                  if (nextUrl1 !== null) {
                    const nextUrl1Params = new URL(nextUrl1);
                    nextUrl1Params.searchParams.set('type', typeAsDisplayName);
                    url.searchParams.set('nextUrl', nextUrl1Params.toString());
                  }
                  return url.toString();
                } else if (productType === IntegrationType.GOOGLESEARCHCONSOLE) {
                  return this.legacyDomainService.buildGoogleAuthUrl(
                    partnerId,
                    accountGroupId,
                    'google-search-console',
                    nextUrl,
                    returnUrl,
                  );
                } else if (productType === IntegrationType.GOOGLEANALYTICS) {
                  return this.legacyDomainService.buildGoogleAuthUrl(
                    partnerId,
                    accountGroupId,
                    'google-analyticsGA4',
                    nextUrl,
                    returnUrl,
                  );
                }

                return serviceAuthURL;
              }),
            );
        }),
      );
  }

  getAuthUrlForMeetings(integrationType: IntegrationType): Observable<string> {
    return this.cardService.listMeetConnection().pipe(
      map((data) => {
        const source = data.sourceInfo.find((source) => {
          return (
            (integrationType === IntegrationType.ZOOMMEET && source.source === MeetingSource.MEETING_SOURCE_ZOOM) ||
            (integrationType === IntegrationType.GOOGLEMEET &&
              source.source === MeetingSource.MEETING_SOURCE_GOOGLE_MEET) ||
            (integrationType === IntegrationType.MICROSOFTTEAMS &&
              source.source === MeetingSource.MEETING_SOURCE_MICROSOFT)
          );
        });
        return source ? source.connectDisconnectUrl : '';
      }),
    );
  }

  getAuthUrlForQuickbooksPersonal(agID: string, userID: string): Observable<string> {
    return this.quickBooksService.getAuthorizationCodeRedirectUrl(agID, userID);
  }

  getAuthUrlForInstagram(partnerId: string, agID: string): Observable<string> {
    const env = this.environmentService.getEnvironmentString();
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const nextURL = `${baseUrl}/account/location/${agID}/settings/connections`;
    const instaAuthUrl =
      `https://instagram-${env}.apigateway.co/v2/auth-user` +
      `?partnerID=${partnerId}` +
      `&businessID=${agID}` +
      `&nextURL=${encodeURIComponent(nextURL)}`;
    return of(instaAuthUrl);
  }

  HandleLegacyIntegration(data: { cardDetail: IntegrationConnectionsDetail; namespace: string }) {
    const integrationType = data.cardDetail.integration?.integrationType as IntegrationType;
    const legacyIntegrationType = this.legacyDomainService.getLegacyIntegrationType(
      integrationType,
    ) as LegacyIntegrationType;
    if (
      integrationType === IntegrationType.GOOGLEMYBUSINESS ||
      integrationType === IntegrationType.FACEBOOK ||
      integrationType === IntegrationType.GOOGLESEARCHCONSOLE ||
      integrationType === IntegrationType.GOOGLEANALYTICS
    ) {
      this.getAuthUrlByIntegrationType(data.namespace, legacyIntegrationType, integrationType).subscribe((finalUrl) => {
        window.location.href = finalUrl;
      });
    } else if (
      integrationType === IntegrationType.GOOGLEMEET ||
      integrationType === IntegrationType.ZOOMMEET ||
      integrationType === IntegrationType.MICROSOFTTEAMS
    ) {
      this.getAuthUrlForMeetings(integrationType).subscribe((finalUrl) => {
        window.location.href = finalUrl;
      });
    } else if (integrationType === IntegrationType.QUICKBOOKSONLINEPERSONAL) {
      this.getAuthUrlForQuickbooksPersonal(data.namespace, this.userID() ?? '').subscribe((finalUrl) => {
        window.location.href = finalUrl;
      });
    } else if (integrationType === IntegrationType.INSTAGRAM) {
      this.getAuthUrlForInstagram(this.partnerID(), data.namespace).subscribe((finalUrl) => {
        window.location.href = finalUrl;
      });
    }
  }
}
